"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import Modal from "@/components/library/Modal";
import { SupportedLanguages } from "@/enums/internationalization";
import AudioPreviewButton from "./AudioPreviewButton";

export default function MultiAudioPreviewButton({
  supportedLanguages,
  setPlaying,
  changeMarker,
  url,
  size = ICON_SIZES.XXL,
}: Readonly<{
  supportedLanguages: SupportedLanguages[];
  setPlaying: (audioPlaying: string) => void;
  changeMarker: string;
  url?: Partial<Record<SupportedLanguages, string>>;
  size?: ICON_SIZES;
}>) {
  const { t } = useTranslation("settings");
  const { t: ct } = useTranslation("common");

  const [showModal, setShowModal] = useState<boolean>(false);

  const handleClose = () => {
    setPlaying("");
    setTimeout(() => {
      //This causes a rerender so we need to wait for the audio to stop playing
      setShowModal(false);
    }, 200);
  };

  return (
    <>
      <Button
        onClick={() => {
          setPlaying("");
          setShowModal(true);
        }}
        type="none"
        className="flex justify-between items-center !p-[6px] border-none rounded-lg"
      >
        <Icon
          icon="circle-play"
          size={size}
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 dark:text-gray-400 transition-colors"
        />
      </Button>
      <Modal
        open={showModal}
        onOpenChange={(open) => !open && handleClose()}
        header={{
          title: t("audioPreview"),
        }}
        buttons={[
          {
            type: "secondary",
            onClick: handleClose,
            children: ct("close"),
          },
        ]}
      >
        <div className="gap-2 grid grid-cols-1 md:grid-cols-2">
          {supportedLanguages
            .filter((lang) => !!url?.[lang])
            .map((lang) => (
              <AudioPreviewButton
                key={lang}
                setPlaying={setPlaying}
                changeMarker={changeMarker}
                url={url?.[lang] ?? ""}
                label={ct(lang)}
              />
            ))}
        </div>
      </Modal>
    </>
  );
}
